import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ATHENS_GRAY } from "@/shared/colors";
import { ErrorModal, HelmetDexteel, getMomentTz } from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { Moment } from "moment-timezone";
import { useState } from "react";
import { Group, Panel, Separator } from "react-resizable-panels";
import { LengthBarsTable } from "./components/LengthBarsTable";
import { LengthBundlesTable } from "./components/LengthBundlesTable";
import { type BarStatusFilter, Filters } from "./components/filters/Filters";
import { useBarsByBundle } from "./hooks/useBarsByBundle";
import { useLengthBundles } from "./hooks/useLengthBundles";

const moment = getMomentTz();

export const BundlesLengthReportPage = () => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const [filterStartDate, setFilterStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [filterEndDate, setFilterEndDate] = useState<Moment>(
		moment().endOf("day"),
	);
	const [filterProductCode, setFilterProductCode] = useState<number | null>(
		null,
	);
	const [barStatusFilter, setBarStatusFilter] = useState<BarStatusFilter>({
		good: true,
		tooLong: true,
		tooShort: true,
	});
	const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null);

	const [bundlesGridApi, setBundlesGridApi] = useState<GridApi | null>(null);

	const { data: bundles, isLoading: isBundlesLoading } = useLengthBundles({
		startDate: filterStartDate.toDate(),
		endDate: filterEndDate.toDate(),
		productCode: filterProductCode,
		onError: (e) => setError(e.message),
	});

	const { data: bars, isLoading: isBarsLoading } = useBarsByBundle({
		bundleId: selectedBundleId,
		onError: (e) => setError(e.message),
	});

	const allSelected =
		barStatusFilter.good && barStatusFilter.tooLong && barStatusFilter.tooShort;

	const filteredBundles = allSelected
		? bundles || []
		: (bundles || []).filter((r) => {
				if (barStatusFilter.tooLong && (r.TooLong ?? 0) > 0) return true;
				if (barStatusFilter.tooShort && (r.TooShort ?? 0) > 0) return true;
				if (barStatusFilter.good && (r.BarCount ?? 0) > (r.BadBars ?? 0))
					return true;
				return false;
			});

	const filteredBars = (bars || []).filter((r) => {
		if (r.IsTooLong) return barStatusFilter.tooLong;
		if (r.IsTooShort) return barStatusFilter.tooShort;
		return barStatusFilter.good;
	});

	const handleFilterChange = (startDate: Moment, endDate: Moment) => {
		setFilterStartDate(startDate);
		setFilterEndDate(endDate);
		setSelectedBundleId(null);
	};

	const handleExportToExcel = () => {
		if (bundlesGridApi) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: "bundles_length_report.xlsx",
			};
			bundlesGridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	const hasExportData = bundles && bundles.length > 0;

	return (
		<>
			<HelmetDexteel title="Bundles Length Report" />
			<Box
				sx={{
					height: "calc(100vh - 64px)",
					display: "flex",
					flexDirection: "column",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				{/* Header */}
				<Box sx={{ px: 2, pt: 2, pb: 1 }}>
					<Grid2 container spacing={1}>
						<Grid2
							size={{ xs: 12 }}
							sx={{ display: "flex", alignItems: "center", gap: 2 }}
						>
							<Typography variant="h5" fontWeight={600}>
								Bundles Length Report
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: "auto" }}>
							<Paper
								elevation={1}
								sx={{
									padding: 2,
									minWidth: 900,
									height: "100%",
									display: "flex",
									alignItems: "center",
								}}
							>
								<Filters
									onFilterChange={handleFilterChange}
									initialStartDate={filterStartDate}
									initialEndDate={filterEndDate}
									productCode={filterProductCode}
									onProductCodeChange={(val) => {
										setFilterProductCode(val);
										setSelectedBundleId(null);
									}}
									barStatusFilter={barStatusFilter}
									onBarStatusFilterChange={setBarStatusFilter}
									showShiftFilter={false}
								/>
							</Paper>
						</Grid2>
						<Grid2 size={{ xs: "grow" }} />
						<Grid2
							size={{ xs: "auto" }}
							sx={{ display: "flex", alignItems: "center" }}
						>
							<ExcelButton
								handleExportToExcel={handleExportToExcel}
								disabled={!hasExportData}
							/>
						</Grid2>
					</Grid2>
				</Box>

				{/* Content */}
				<Box sx={{ flex: 1, overflow: "hidden", px: 2, pb: 2 }}>
					<Group orientation="horizontal" style={{ height: "100%" }}>
						<Panel id="bundles-panel" defaultSize={60} minSize={30}>
							<LengthBundlesTable
								isLoading={isBundlesLoading}
								rows={filteredBundles}
								setGridApi={setBundlesGridApi}
								onRowClicked={setSelectedBundleId}
							/>
						</Panel>

						<Separator
							style={{
								width: 8,
								backgroundColor: "#e0e0e0",
								cursor: "col-resize",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Box
								sx={{
									width: 4,
									height: 40,
									backgroundColor: "#bdbdbd",
									borderRadius: 2,
								}}
							/>
						</Separator>

						<Panel id="bars-panel" defaultSize={40} minSize={20}>
							<LengthBarsTable
								isLoading={isBarsLoading}
								rows={filteredBars}
								selectedBundleId={selectedBundleId}
							/>
						</Panel>
					</Group>
				</Box>
			</Box>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
